import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, ArrowRight, ChevronDown, Search } from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventsPage = () => {
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [events, setEvents] = useState([]);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [featuredRes, genresRes] = await Promise.all([
          axios.get(`${API}/events/featured-list?limit=2`),
          axios.get(`${API}/genres`),
        ]);
        setFeaturedEvents(Array.isArray(featuredRes.data) ? featuredRes.data : []);
        setGenres(genresRes.data);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    fetchInitial();
  }, []);

  // Debounced server-side search + genre filter
  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (searchQuery.trim()) params.set('q', searchQuery.trim());
        if (selectedGenre !== 'all') params.set('genre', selectedGenre);
        const res = await axios.get(`${API}/events${params.toString() ? `?${params}` : ''}`);
        if (!cancelled) setEvents(res.data);
      } catch (error) {
        console.error('Error searching events:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [searchQuery, selectedGenre]);

  // Hide events already featured at the top of the page from the grid
  // (backend guarantees no dupes across the two slots).
  const featuredIds = new Set(featuredEvents.map((e) => e.id));
  const filteredEvents = events.filter((e) => !featuredIds.has(e.id));

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    }).toUpperCase();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="loading">
        <div className="text-[#B5FF00] font-mono uppercase tracking-widest animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="events-page">
      <Seo title="Events · London, Mumbai, Bangalore, Goa" description="Every verified underground event happening across the UK and India — grassroot, house, drum & bass, minimal, more. Filter by city and buy tickets in one tap." path="/events" />
      {/* Hero Section — primary featured event, keeps the full-bleed cinematic look */}
      {featuredEvents[0] && (() => {
        const featuredEvent = featuredEvents[0];
        return (
        <section className="relative min-h-screen flex items-end" data-testid="hero-section">
          <div className="absolute inset-0">
            <img 
              src={featuredEvent.image_url} 
              alt={featuredEvent.title}
              className="w-full h-full object-cover img-grayscale"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 to-transparent" />
          </div>

          <div className="relative container mx-auto px-4 md:px-8 pb-16 md:pb-24 pt-32">
            <div className="max-w-4xl">
              <div className="mb-4 opacity-0 animate-fade-in-up stagger-1" style={{ animationFillMode: 'forwards' }}>
                <span className="font-mono text-xs tracking-widest text-[#B5FF00] border border-[#B5FF00]/30 px-3 py-1">
                  {featuredEvent.genre.toUpperCase()}
                </span>
              </div>

              <h1 
                className="font-['Josefin_Sans'] text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-none mb-6 opacity-0 animate-fade-in-up stagger-2"
                style={{ animationFillMode: 'forwards' }}
                data-testid="hero-title"
              >
                {featuredEvent.title}
              </h1>

              <div className="flex flex-wrap gap-6 mb-8 opacity-0 animate-fade-in-up stagger-3" style={{ animationFillMode: 'forwards' }}>
                <div className="flex items-center gap-2 text-zinc-400">
                  <Calendar size={18} strokeWidth={1.5} className="text-[#B5FF00]" />
                  <span className="font-mono text-sm tracking-wider">
                    {formatDate(featuredEvent.date)} • {featuredEvent.time}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-zinc-400">
                  <MapPin size={18} strokeWidth={1.5} className="text-[#B5FF00]" />
                  <span className="font-mono text-sm tracking-wider">
                    {featuredEvent.venue}
                  </span>
                </div>
              </div>

              <p className="text-zinc-400 text-lg max-w-xl mb-8 opacity-0 animate-fade-in-up stagger-4" style={{ animationFillMode: 'forwards' }}>
                {featuredEvent.description}
              </p>

              <div className="opacity-0 animate-fade-in-up stagger-5" style={{ animationFillMode: 'forwards' }}>
                <Link 
                  to={`/events/${featuredEvent.id}`}
                  className="btn-primary inline-flex items-center gap-3 group"
                  data-testid="hero-cta"
                >
                  {featuredEvent.waitlist_only ? 'JOIN WAITLIST' : 'GET TICKETS'}
                  <ArrowRight size={18} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown size={24} strokeWidth={1.5} className="text-zinc-600" />
          </div>
        </section>
        );
      })()}

      {/* Second featured slot — a compact "Also Featured" card that runs
          immediately below the hero. Fills automatically when only 1 event
          is `is_featured=true` (backend backfills with next upcoming). */}
      {featuredEvents[1] && (
        <section className="border-t border-zinc-800 bg-[#050505]" data-testid="second-featured-section">
          <div className="container mx-auto px-4 md:px-8 py-12 md:py-16">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs tracking-widest text-[#B5FF00]">ALSO FEATURED</span>
              <span className="flex-1 h-px bg-zinc-800" />
            </div>
            <Link
              to={`/events/${featuredEvents[1].id}`}
              className="group grid md:grid-cols-5 gap-6 md:gap-10 items-center border border-zinc-800 hover:border-[#B5FF00]/40 transition-colors p-4 md:p-6 bg-[#0A0A0A]"
              data-testid={`second-featured-${featuredEvents[1].id}`}
            >
              <div className="md:col-span-2 aspect-[4/3] overflow-hidden">
                <img
                  src={featuredEvents[1].image_url}
                  alt={featuredEvents[1].title}
                  className="w-full h-full object-cover img-grayscale group-hover:img-none transition-all duration-500 group-hover:scale-105"
                />
              </div>
              <div className="md:col-span-3">
                <span className="font-mono text-xs tracking-widest text-[#B5FF00] border border-[#B5FF00]/30 px-3 py-1">
                  {(featuredEvents[1].genre || '').toUpperCase()}
                </span>
                <h2 className="font-['Josefin_Sans'] text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mt-4 mb-4 group-hover:text-[#B5FF00] transition-colors">
                  {featuredEvents[1].title}
                </h2>
                <div className="flex flex-wrap gap-4 md:gap-6 mb-4 text-zinc-400">
                  <div className="flex items-center gap-2">
                    <Calendar size={16} strokeWidth={1.5} className="text-[#B5FF00]" />
                    <span className="font-mono text-sm tracking-wider">
                      {formatDate(featuredEvents[1].date)} • {featuredEvents[1].time}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={16} strokeWidth={1.5} className="text-[#B5FF00]" />
                    <span className="font-mono text-sm tracking-wider">{featuredEvents[1].venue}</span>
                  </div>
                </div>
                <p className="text-zinc-500 text-sm md:text-base line-clamp-2 mb-4">
                  {featuredEvents[1].description}
                </p>
                <span className="inline-flex items-center gap-2 font-mono text-sm tracking-widest text-white group-hover:text-[#B5FF00] transition-colors">
                  {featuredEvents[1].waitlist_only ? 'JOIN WAITLIST' : 'GET TICKETS'}
                  <ArrowRight size={16} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Events Grid Section */}
      <section className="py-16 md:py-24 border-t border-zinc-800" data-testid="events-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-2 block">
                UPCOMING
              </span>
              <h2 className="font-['Josefin_Sans'] text-4xl md:text-5xl font-bold text-white">
                ALL EVENTS
              </h2>
            </div>

            <div className="w-full md:w-auto md:flex md:items-center md:gap-3">
              <div className="relative md:w-64 mb-3 md:mb-0">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search title, venue, city..."
                  className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white pl-12 pr-4 focus:border-[#B5FF00] focus:outline-none"
                  data-testid="events-search-input"
                />
              </div>
              <div className="w-full md:w-48">
                <Select value={selectedGenre} onValueChange={setSelectedGenre}>
                  <SelectTrigger 
                    className="bg-[#0A0A0A] border-zinc-800 text-white h-12 rounded-none focus:ring-[#B5FF00] focus:ring-1"
                    data-testid="genre-filter"
                  >
                    <SelectValue placeholder="All Genres" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-zinc-800">
                    <SelectItem value="all" className="text-white focus:bg-zinc-800 focus:text-white">
                      All Genres
                    </SelectItem>
                    {genres.map((genre) => (
                      <SelectItem 
                        key={genre} 
                        value={genre}
                        className="text-white focus:bg-zinc-800 focus:text-white"
                      >
                        {genre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1" data-testid="events-grid">
            {filteredEvents.map((event, index) => (
              <EventCard key={event.id} event={event} index={index} />
            ))}
          </div>

          {filteredEvents.length === 0 && (
            <div className="text-center py-16">
              <p className="text-zinc-500 font-mono uppercase tracking-widest">
                No events found
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <span className="footer-brand">UNIGHT</span>
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_venue-pulse-14/artifacts/8f7het1z_Unight%202d%20Logos%20%28TM%29-05.png"
              alt="Unight"
              className="h-8"
            />
            <span className="font-mono text-xs text-zinc-600 tracking-widest">
              © 2025 UNIGHT. ALL RIGHTS RESERVED.
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

const EventCard = ({ event, index }) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase()
    };
  };

  const { day, month } = formatDate(event.date);
  const hasTiers = Array.isArray(event.ticket_tiers) && event.ticket_tiers.length > 0;
  const lowestPrice = hasTiers ? Math.min(...event.ticket_tiers.map((t) => t.price)) : null;
  const isWaitlist = !!event.waitlist_only;

  return (
    <Link 
      to={`/events/${event.id}`}
      className="event-card corner-brackets group h-[400px] md:h-[450px] flex flex-col opacity-0 animate-fade-in-up"
      style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
      data-testid={`event-card-${event.id}`}
    >
      <div className="absolute inset-0">
        <img 
          src={event.image_url} 
          alt={event.title}
          className="w-full h-full object-cover img-grayscale group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      <div className="relative z-10 mt-auto p-6 md:p-8">
        <div className="absolute top-6 right-6 bg-[#050505]/80 backdrop-blur-sm border border-zinc-800 p-3 text-center">
          <div className="font-['Josefin_Sans'] text-2xl font-bold text-white leading-none">{day}</div>
          <div className="font-mono text-xs text-[#B5FF00] tracking-wider">{month}</div>
        </div>

        <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-2 block">
          {event.genre.toUpperCase()}
        </span>

        <h3 className="font-['Josefin_Sans'] text-2xl md:text-3xl font-bold text-white mb-2 group-hover:text-[#B5FF00] transition-colors">
          {event.title}
        </h3>

        <p className="text-zinc-400 text-sm mb-4 flex items-center gap-2">
          <MapPin size={14} strokeWidth={1.5} />
          {event.venue}
        </p>

        <div className="flex items-center justify-between">
          <span className="font-mono text-sm text-zinc-400">
            {isWaitlist ? (
              <span className="text-[#B5FF00]">WAITLIST OPEN</span>
            ) : hasTiers ? (
              <>FROM <span className="text-white">₹{lowestPrice.toFixed(0)}</span></>
            ) : (
              <span className="text-zinc-500">COMING SOON</span>
            )}
          </span>
          <span className="text-[#B5FF00] font-mono text-xs tracking-widest opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
            {isWaitlist ? 'JOIN WAITLIST' : 'VIEW EVENT'} <ArrowRight size={14} strokeWidth={2} />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default EventsPage;
